import { cloneDeep, escape } from 'lodash-es';
import { SurveyDefinition, SurveyLookAndFeelSettings, SurveyStepDefinition, WebViewStepDefinition, WebViewStepTemplate } from '../../../types/Survey';
import { ConsentSignatureResultModel, QuestionResultModel, SurveyResultModel, WebViewStepResultModel } from '../../../types/SurveyResult';
import { makeCssVars } from '../../../helpers/surveyLookAndFeelHelper';
import { DARK_SCHEME_COLORS } from '../../../helpers/surveyStyleDefaults';

export type WebViewContentMode = 'fullHtml' | 'partialHtml' | 'specifyUrl';

/** QuestionResultModel augmented with legacy `Answer` and `Choices` fields for use in WebViewStep iframe content. */
export interface LegacyQuestionResultModel extends QuestionResultModel {
    Answer: string;
    Choices: string[];
}

export interface WebViewSurveyAnswer {
    date?: string;
    stepIdentifier: string;
    resultIdentifier: string;
    answers: string[];
    // ConsentReviewStep only
    consented?: boolean;
    givenName?: string;
    familyName?: string;
}

/**
 * Legacy `Answer` (first answer) and `Choices` (all answers) fields are what the WebViewStep
 * iframe content reads, so the injected taskResult must carry them.
 * Returns a clone; the original result is not mutated.
 */
export function populateLegacyAnswerAndChoicesForQuestionSteps(taskResult: SurveyResultModel, surveyDefinition: SurveyDefinition): SurveyResultModel {
    if (!taskResult?.StepResults) return taskResult;

    const cloned: SurveyResultModel = cloneDeep(taskResult);

    for (const stepResult of cloned.StepResults) {
        var step = surveyDefinition.steps.find( function (s) {
            return s.identifier === stepResult.Identifier;
        });

        if (!stepResult.QuestionResults || !step) continue;

        for (const questionResult of stepResult.QuestionResults) {
            if (!questionResult || questionResult.Type !== 'QuestionResult') continue;

            const qr = questionResult as LegacyQuestionResultModel;
            const answers = (qr as QuestionResultModel).Answers ?? [];
            qr.Answer = answers[0] ?? '';
            qr.Choices = answers;
        }
    }

    return cloned;
}


export function buildCurrentSurveyAnswers(
    taskResult: SurveyResultModel,
    steps: SurveyStepDefinition[],
): WebViewSurveyAnswer[] {
    const validStepTypes = ['QuestionStep', 'FormStep', 'WebViewStep', 'ConsentReviewStep'];
    const surveyAnswers: WebViewSurveyAnswer[] = [];

    for (const stepResult of taskResult.StepResults) {
        const step = steps.find(
            s => s.identifier === stepResult.Identifier && validStepTypes.includes(s.type),
        );
        if (!step) continue;

        for (const questionResult of stepResult.QuestionResults) {
            const surveyAnswer: WebViewSurveyAnswer = {
                date: questionResult.EndDate,
                stepIdentifier: stepResult.Identifier,
                resultIdentifier: questionResult.Identifier,
                answers: [],
            };

            if (step.type === 'ConsentReviewStep') {
                const consentResult = questionResult as ConsentSignatureResultModel;
                surveyAnswer.consented = consentResult.Consented;
                surveyAnswer.givenName = consentResult.Signature?.GivenName;
                surveyAnswer.familyName = consentResult.Signature?.FamilyName;
            }

            const typedResult = questionResult as QuestionResultModel | WebViewStepResultModel;
            if ('Answers' in typedResult && typedResult.Answers) {
                surveyAnswer.answers.push(...typedResult.Answers);
            }
            if ('Result' in typedResult && typedResult.Result) {
                surveyAnswer.answers.push(typedResult.Result);
            }

            surveyAnswers.push(surveyAnswer);
        }
    }

    return surveyAnswers;
}

function escapeHtml(html: string): string {
    const text = document.createTextNode(html);
    const p = document.createElement('p');
    p.appendChild(text);
    return p.innerHTML;
}

export function getStepHtml(
    step: WebViewStepDefinition,
    webViewStepTemplates: WebViewStepTemplate[] | undefined,
): string {
    if (step.templateIdentifier) {
        const stepTemplate = webViewStepTemplates?.find(t => t.identifier === step.templateIdentifier);
        if (!stepTemplate) return '';
        let html = stepTemplate.html;
        for (const field of stepTemplate.fields) {
            const fieldValue = step.fieldValues?.[field.identifier];
            let value = fieldValue || field.defaultValue || '';
            if (field.editorType !== 'html') {
                value = escapeHtml(value);
            }
            html = html.replace(new RegExp('<%\\s*TemplateField\\.' + field.identifier + '\\s*%>', 'g'), value);
        }
        return html;
    }
    return step.html ?? '';
}

export function acceptableEventSource(
    event: MessageEvent,
    frameUrl: string | undefined | null,
    contentWindow: Window | null | undefined,
): boolean {
    if (!frameUrl) return false;
    const frameUrlParsed = new URL(frameUrl);
    const originUrl = new URL(event.origin);
    if (originUrl.hostname !== frameUrlParsed.hostname) return false;
    return !!contentWindow && event.source === contentWindow;
}

// Precedence: an explicit `contentMode`, else `specifyUrl` when a `url` is set, else `fullHtml`.
export function getWebViewContentMode(step: WebViewStepDefinition): WebViewContentMode {
    return (step.contentMode as WebViewContentMode) || (step.url ? 'specifyUrl' : 'fullHtml');
}

// Returns undefined for an invalid or non-http(s) url so the caller keeps the previous frame
// url; otherwise the resolved href with the active language appended as a `lang` query param.
export function buildFrameUrl(
    stepUrl: string | undefined,
    defaultFrameUrl: string,
    language: string | undefined,
    colorScheme: string,
): string | undefined {
    const tempUrl = stepUrl ? stepUrl : defaultFrameUrl;
    try {
        const checkUrl = new URL(tempUrl);
        if (language) {
            checkUrl.searchParams.set('lang', language);
        }
        checkUrl.searchParams.set('colorScheme', colorScheme);
        if (['http:', 'https:'].includes(checkUrl.protocol)) {
            return checkUrl.href;
        }
    } catch {
        return undefined;
    }
    return undefined;
}

// Embedded (app) mode adds an allow-top-navigation-by-user-activation token to the sandbox.
export function buildSandboxAttributes(isEmbedded: boolean): string {
    let attributes = 'allow-forms allow-scripts allow-same-origin allow-presentation allow-popups allow-popups-to-escape-sandbox';
    if (isEmbedded) {
        attributes += ' allow-top-navigation-by-user-activation';
    }
    return attributes;
}

export function getCssVariableOverrides(surveyDefinition: SurveyDefinition, step: WebViewStepDefinition): string {
    const styleSettings = { ...surveyDefinition.styles, ...step.styles } as SurveyLookAndFeelSettings;
    const styleVars = makeCssVars(styleSettings);
    return styleVars.length ? `<style>${styleVars}</style>` : '';
}

export interface AssembleWebViewContentParams {
    step: WebViewStepDefinition;
    surveyDefinition: SurveyDefinition;
    surveyResult: SurveyResultModel;
    baseUrl: string;
    webViewContentHtml: string;
    webViewStructuredHtml: string;
    colorScheme: string;
}

export function assembleWebViewContent(params: AssembleWebViewContentParams): string {
    const { step, surveyDefinition, surveyResult, baseUrl, webViewContentHtml, webViewStructuredHtml, colorScheme } = params;

    const mode = getWebViewContentMode(step);

    let webContent = getStepHtml(step, surveyDefinition.webViewStepTemplates);

    if (mode === 'partialHtml') {
        webContent = webViewStructuredHtml.replace(/{\s?html\s?}/g, webContent);
        webContent = webContent.replace(/{\s?cssVariableOverrides\s?}/g, getCssVariableOverrides(surveyDefinition, step));
        webContent = webContent.replace(/{\s?colorScheme\s?}/g, colorScheme);
        webContent = webContent.replace(/{dark\.(\w+)}/g, (_match, name: string) => DARK_SCHEME_COLORS[name as keyof typeof DARK_SCHEME_COLORS] ?? '');
    }

    let prependContent = webViewContentHtml;
    const taskResult = populateLegacyAnswerAndChoicesForQuestionSteps(surveyResult, surveyDefinition);
    const taskResultEncoded = escape(JSON.stringify(taskResult));
    prependContent = prependContent.replace(/{\s?taskResult\s?}/g, taskResultEncoded);
    const baseUrlEncoded = escape(baseUrl);
    prependContent = prependContent.replace(/{\s?baseUrl\s?}/g, baseUrlEncoded);
    prependContent = prependContent.replace(/{\s?colorScheme\s?}/g, colorScheme);

    return prependContent + webContent;
}

/** Survey mode reported to WebViewStep content via the GetSurveyContext bridge action. */
export type SurveyMode = 'Design' | 'Preview' | 'Survey';

export interface WebViewStepConfiguration {
    /** Merged survey + step styles (step overrides survey). */
    styles: Record<string, any>;
    properties?: Record<string, unknown>;
}

export interface WebViewSurveyContext {
    surveyMode: SurveyMode;
}

/**
 * The `data` payload the host returns for a `GetStepConfiguration` bridge request:
 * merged survey + step styles (step wins) plus the step's custom properties. Extracted as a
 * pure function so the response contract can be golden-mastered in webViewStepUtils.test.ts.
 */
export function buildStepConfiguration(surveyDefinition: SurveyDefinition, step: WebViewStepDefinition): WebViewStepConfiguration {
    return {
        styles: { ...surveyDefinition.styles, ...step.styles },
        properties: step.properties,
    };
}

/** The `data` payload the host returns for a `GetSurveyContext` bridge request. */
export function buildSurveyContext(mode: SurveyMode): WebViewSurveyContext {
    return { surveyMode: mode };
}